import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { usePrivy, useLoginWithOAuth } from '@privy-io/react-auth';
import { Capacitor } from '@capacitor/core';
import WidthContainer from '../../components/WidthContainer';
import GiftCard from '../../components/GiftCard';
import ApplePayButton from '../../components/ApplePayButton';
import TextInput from '../../components/Inputs/Text';
import { getCheckoutTokenAddress } from '../../common/checkoutTokens';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
} from '../../redux/slices/tokenSlice';
import { selectTokenBalance } from '../../redux/slices/walletSlice';
import { useLazySearchTokensQuery } from '../../redux/api/public/tokens';
import {
  calculateApplePayFee,
  BALANCE_TOLERANCE,
} from '../../hooks/useApplePay';
import { formatUSD } from '../../common/currency';
import useApplePayCheckout from '../../hooks/useApplePayCheckout';
import { ApplePayWidget } from '../../components/ApplePayWidget';
import { useSolana } from '../../hooks/useSolana';
import { useGetCurrentUserProfileQuery } from '../../redux/api/authorized/auth';
import {
  useCreateGiftMutation,
  usePrepareTransactionsMutation,
  useBroadcastAndTrackMutation,
} from '../../redux/api/authorized/gifts';
import { useCreateCustomGiftCardDesignMutation } from '../../redux/api/authorized/customGiftCardDesigns';
import { compressBase64Image } from '../../common/images';
import { toTokenAmount } from '../../hooks/useSolana';
import SendingStatus from '../Gifts/SendingStatus';
import { openModal, closeModal } from '../../redux/slices/modals';

const CHECKOUT_NETWORK = 'solana';

const AMOUNTS = [5, 25, 50, 100];
const MIN_APPLE_PAY_AMOUNT = 5;

const Content = styled.div`
  padding-bottom: 120px;
  margin-left: auto;
  margin-right: auto;
  padding-top: 20px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.text.title};
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.text.secondary};
  text-align: center;
  margin-bottom: 12px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const Section = styled.section`
  width: 100%;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.text.secondary};
  margin-bottom: 8px;
`;

const InputWrap = styled.div`
  margin-bottom: 16px;
`;

const InfoBlock = styled.div`
  padding: 16px 0px;
  border-radius: 12px;
`;

const InfoTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  margin-bottom: 8px;
`;

const InfoText = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.text.secondary};
  line-height: 1.5;
  margin: 0;
`;

const AmountButton = styled.button`
  flex: 1;
  border-radius: 16px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? `${props.theme.primary.primary}`
        : `${props.theme.border.primary}`};
  padding: 12px 12px;
  background-color: ${(props) =>
    props.$active
      ? `${props.theme.background.primary}`
      : `${props.theme.background.light}`};
  color: ${({ theme }) => theme.text.title};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$active
          ? `${props.theme.primary.primary}`
          : `${props.theme.text.disabled}`};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AmountButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const PaySection = styled.div`
  padding: 50px 0px 20px 0px;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  background: linear-gradient(
    to top,
    ${({ theme }) => theme.background.primary} 0%,
    ${({ theme }) => theme.background.primary} 60%,
    transparent 100%
  );
`;

const FeeText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.text.secondary};
  text-align: center;
  margin-top: 8px;
`;

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 600px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const FeedbackMessage = styled.p`
  font-size: 14px;
  color: ${({ theme, $isError }) =>
    $isError ? theme.status.error.primary : theme.text.secondary};
  text-align: center;
  margin-top: 8px;
`;

export default function Checkout() {
  const { t } = useTranslation();
  const { tokenName } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const tokenAddress = getCheckoutTokenAddress(tokenName);
  const token = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, CHECKOUT_NETWORK, tokenAddress ?? ''),
  );
  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, CHECKOUT_NETWORK, 'USDC'),
  );
  const usdcTokenBalance = useSelector((state) =>
    selectTokenBalance(CHECKOUT_NETWORK, usdcToken?.address ?? '')(state),
  );
  const [searchTokens] = useLazySearchTokensQuery();

  const { authenticated } = usePrivy();
  const { loading: oauthLoading, initOAuth } = useLoginWithOAuth();
  const { data: currentUser, isSuccess: isUserProfileFetched } =
    useGetCurrentUserProfileQuery(undefined, { skip: !authenticated });
  const { refreshTokenBalances } = useSolana();
  const hasVerifiedPhone = Boolean(
    currentUser?.phoneVerifiedAt ?? currentUser?.phone_verified_at,
  );

  const [loading, setLoading] = useState(false);
  const [sendingStatus, setSendingStatus] = useState('idle');
  const [claimUrl, setClaimUrl] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [feedbackIsError, setFeedbackIsError] = useState(true);
  const [pendingApplePayUsdAmount, setPendingApplePayUsdAmount] = useState(null);
  const [waitingForApplePayBalance, setWaitingForApplePayBalance] =
    useState(false);
  const [finalImageUrl, setFinalImageUrl] = useState(null);

  const hasOpenedPhoneVerificationRef = useRef(false);

  const {
    startCheckout: startApplePayCheckout,
    widgetProps: applePayWidgetProps,
    isStarting: applePayStarting,
    isProcessingPayment: applePayProcessing,
    reset: resetApplePayCheckout,
  } = useApplePayCheckout();

  const [createGiftMutation] = useCreateGiftMutation();
  const [prepareTransactions] = usePrepareTransactionsMutation();
  const [broadcastAndTrack] = useBroadcastAndTrackMutation();
  const [createCustomGiftCardDesign] = useCreateCustomGiftCardDesignMutation();
  const {
    signAndSerializeTransaction,
    parseSolanaError,
  } = useSolana();

  const amountFromQueryOnMount = searchParams.get('amount');
  const paymentStarted = searchParams.get('paymentStarted') === '1';

  const initialAmount =
    amountFromQueryOnMount && AMOUNTS.includes(Number(amountFromQueryOnMount))
      ? Number(amountFromQueryOnMount)
      : 25;

  const [selectedAmount, setSelectedAmount] = useState(initialAmount);
  const [cardImage, setCardImage] = useState(null);
  const [senderName, setSenderName] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const amountFromQuery = searchParams.get('amount');
  const resolvedAmount =
    amountFromQuery && AMOUNTS.includes(Number(amountFromQuery))
      ? Number(amountFromQuery)
      : selectedAmount;

  const isWeb = !Capacitor.isNativePlatform();
  const applePayAutoStartedRef = useRef(false);

  useEffect(() => {
    if (!tokenAddress) {
      navigate('/', { replace: true });
      return;
    }
    searchTokens({ addresses: [tokenAddress] });
  }, [tokenAddress, searchTokens, navigate]);

  // Save local data URL image to backend when user is logged in
  useEffect(() => {
    const saveLocalImageIfNeeded = async () => {
      if (!cardImage || !cardImage.startsWith('data:')) return;
      if (!authenticated) return;

      try {
        const compressedImage = await compressBase64Image(cardImage);
        const response = await createCustomGiftCardDesign({
          image: compressedImage,
          type: 'digital',
        });
        if (response.data?.imageUrl) {
          setFinalImageUrl(response.data.imageUrl);
        }
      } catch (error) {
        console.error('Failed to save local image:', error);
      }
    };
    saveLocalImageIfNeeded();
  }, [cardImage, authenticated, createCustomGiftCardDesign]);

  // Poll for balance updates while waiting for Apple Pay funds
  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshTokenBalances({ force: true });
    }, 3000);
    return () => clearInterval(intervalId);
  }, [refreshTokenBalances]);

  const createLinkGift = useCallback(async () => {
    if (!token?.address || !usdcToken?.address) {
      throw new Error('Missing required tokens');
    }

    let imageUrl = finalImageUrl || (cardImage?.startsWith('http') ? cardImage : null);
    if (!imageUrl && cardImage?.startsWith('data:')) {
      try {
        const compressed = await compressBase64Image(cardImage);
        const resp = await createCustomGiftCardDesign({
          image: compressed,
          type: 'digital',
        });
        imageUrl = resp.data?.imageUrl ?? null;
      } catch (err) {
        console.error('Failed to save card image:', err);
      }
    }
    const giftData = {
      delivery: 'link',
      token: token.address,
      baseToken: usdcToken.address,
      network: CHECKOUT_NETWORK,
      imageUrl,
      message: personalMessage,
      senderName,
      amountSpecifiedInUsd: true,
      baseTokenAmount: toTokenAmount(resolvedAmount, usdcToken.decimals),
      email: null,
    };

    const giftResponse = await createGiftMutation(giftData).unwrap();
    const createdGift = giftResponse?.gift;

    setFeedback('Preparing transactions...');
    const prepareResult = await prepareTransactions(createdGift.id).unwrap();

    if (prepareResult.swapTransaction) {
      const signedSwapTx = await signAndSerializeTransaction(
        prepareResult.swapTransaction,
      );
      const signedSecondTx = await signAndSerializeTransaction(
        prepareResult.htlcTransaction || prepareResult.transferTransaction,
      );
      setFeedback('Broadcasting transactions...');
      const result = await broadcastAndTrack({
        giftId: createdGift.id,
        serializedTransaction: signedSwapTx,
        serializedSecondTransaction: signedSecondTx,
        requestId: prepareResult.requestId,
      }).unwrap();
      setClaimUrl(result.gift?.claimUrl);
      if (result.transactionId) {
        setSendingStatus('success');
        setFeedback(null);
      }
    } else {
      const signedTx = await signAndSerializeTransaction(
        prepareResult.serializedTransaction,
      );
      setFeedback('Broadcasting transaction...');
      const result = await broadcastAndTrack({
        giftId: createdGift.id,
        serializedTransaction: signedTx,
      }).unwrap();
      setClaimUrl(result.gift?.claimUrl);
      if (result.transactionId) {
        setSendingStatus('success');
        setFeedback(null);
      }
    }
    refreshTokenBalances();
  }, [
    token,
    usdcToken,
    finalImageUrl,
    cardImage,
    personalMessage,
    senderName,
    resolvedAmount,
    createGiftMutation,
    prepareTransactions,
    broadcastAndTrack,
    signAndSerializeTransaction,
    refreshTokenBalances,
    createCustomGiftCardDesign,
  ]);

  const startApplePayFlow = useCallback(() => {
    if (!resolvedAmount || resolvedAmount < MIN_APPLE_PAY_AMOUNT) {
      setFeedbackIsError(true);
      setFeedback(t('checkoutPage.applePayMinAmount', { amount: MIN_APPLE_PAY_AMOUNT }));
      return;
    }

    startApplePayCheckout(resolvedAmount, {
      onSuccess: () => {
        setFeedback(t('checkoutPage.waitingForFunds'));
        setFeedbackIsError(false);
        setPendingApplePayUsdAmount(resolvedAmount);
        setWaitingForApplePayBalance(true);
      },
      onPhoneVerificationExpired: () => {
        openPhoneVerificationModal();
        setFeedback(null);
        setFeedbackIsError(true);
        resetApplePayCheckout();
        setPendingApplePayUsdAmount(null);
        setWaitingForApplePayBalance(false);
      },
      onError: (err) => {
        setFeedbackIsError(true);
        setFeedback(
          err
            ? t('checkoutPage.applePayStartError')
            : t('checkoutPage.applePayPaymentError'),
        );
        resetApplePayCheckout();
        setPendingApplePayUsdAmount(null);
        setWaitingForApplePayBalance(false);
      },
      onAlert: (title, message, type) => {
        if (type === 'error') {
          setFeedbackIsError(true);
          setFeedback(
            `Apple Pay: ${title || message || t('checkoutPage.applePayPaymentError')}`,
          );
          resetApplePayCheckout();
          setPendingApplePayUsdAmount(null);
          setWaitingForApplePayBalance(false);
        } else if (type === 'info') {
          setFeedbackIsError(false);
          setFeedback(message || title || null);
        }
      },
    }).catch(() => {});
  }, [resolvedAmount, startApplePayCheckout, resetApplePayCheckout, t]);

  const openPhoneVerificationModal = useCallback(() => {
    dispatch(
      openModal({
        name: 'verifyPhoneNumber',
        props: {
          onContinue: () => {
            dispatch(closeModal());
            startApplePayFlow();
          },
        },
      }),
    );
  }, [dispatch, startApplePayFlow]);

  const handleApplePayPress = useCallback(() => {
    if (!authenticated) {
      setLoading(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('paymentStarted', '1');
          next.set('amount', String(resolvedAmount));
          return next;
        },
        { replace: true },
      );
      initOAuth({ provider: 'apple' }).finally(() => setLoading(false));
      return;
    }

    if (isUserProfileFetched && !hasVerifiedPhone) {
      openPhoneVerificationModal();
      return;
    }

    if (!isUserProfileFetched) return;

    startApplePayFlow();
  }, [
    authenticated,
    isUserProfileFetched,
    hasVerifiedPhone,
    initOAuth,
    setSearchParams,
    startApplePayFlow,
    resolvedAmount,
    openPhoneVerificationModal,
  ]);

  // When payment started and authenticated but phone not verified: open phone verification modal (only after user profile is fetched)
  useEffect(() => {
    if (!paymentStarted || !authenticated || !isUserProfileFetched) {
      if (!paymentStarted) hasOpenedPhoneVerificationRef.current = false;
      return;
    }
    if (hasVerifiedPhone) return;
    if (hasOpenedPhoneVerificationRef.current) return;
    hasOpenedPhoneVerificationRef.current = true;
    dispatch(
      openModal({
        name: 'verifyPhoneNumber',
        props: {
          onContinue: () => {
            dispatch(closeModal());
            startApplePayFlow();
          },
        },
      }),
    );
  }, [
    paymentStarted,
    authenticated,
    isUserProfileFetched,
    hasVerifiedPhone,
    dispatch,
    startApplePayFlow,
  ]);

  // Auto-start checkout when paymentStarted + authenticated + hasVerifiedPhone (e.g. returned from OAuth)
  useEffect(() => {
    if (!paymentStarted || !authenticated || !hasVerifiedPhone) return;
    if (applePayWidgetProps || applePayStarting) return;
    if (applePayAutoStartedRef.current) return;
    if (resolvedAmount < MIN_APPLE_PAY_AMOUNT) return;

    applePayAutoStartedRef.current = true;
    startApplePayFlow();
  }, [
    paymentStarted,
    authenticated,
    hasVerifiedPhone,
    applePayWidgetProps,
    applePayStarting,
    resolvedAmount,
    startApplePayFlow,
  ]);

  // Wait for Apple Pay funds, then create link gift
  useEffect(() => {
    if (!waitingForApplePayBalance || pendingApplePayUsdAmount == null) return;

    const numericBalance = parseFloat(usdcTokenBalance ?? '0');
    if (Number.isNaN(numericBalance)) return;

    const requiredBalance = Math.max(
      0,
      pendingApplePayUsdAmount - BALANCE_TOLERANCE,
    );
    if (numericBalance >= requiredBalance) {
      setWaitingForApplePayBalance(false);
      setPendingApplePayUsdAmount(null);
      setFeedback(t('checkoutPage.paymentProcessing'));
      setFeedbackIsError(false);
      resetApplePayCheckout();
      setSendingStatus('sending');
      createLinkGift().catch((err) => {
        setSendingStatus('error');
        setFeedbackIsError(true);
        setFeedback(parseSolanaError(err));
      });
    }
  }, [
    waitingForApplePayBalance,
    pendingApplePayUsdAmount,
    usdcTokenBalance,
    createLinkGift,
    resetApplePayCheckout,
    parseSolanaError,
    t,
  ]);

  useEffect(() => {
    return () => {
      resetApplePayCheckout();
      setPendingApplePayUsdAmount(null);
      setWaitingForApplePayBalance(false);
    };
  }, [resetApplePayCheckout]);

  if (!tokenAddress) {
    return null;
  }

  if (!token) {
    return null; // Token not yet loaded from RTX/DB
  }

  const tokenLabel = token.name || token.symbol || tokenName;
  const giftCardToken = {
    address: token.address,
    symbol: token.symbol,
    logoURI: token.logoURI,
    giftCardImages: token.giftCardImages ?? [],
  };

  const applePayFee = calculateApplePayFee(resolvedAmount);

  // Success or sending state: show SendingStatus
  if (sendingStatus === 'sending' || sendingStatus === 'success') {
    return (
      <Content>
        <SendingStatus
          giftUrl={claimUrl}
          token={token}
          cardImage={cardImage}
          usdAmount={resolvedAmount}
          tokenAmount={undefined}
          isSuccess={sendingStatus === 'success'}
          isPhysical={false}
        />
      </Content>
    );
  }

  return (
    <Content>
      <ContentContainer>
        <div>
          <Title>
            {t('checkoutPage.giftCardTitle', {
              tokenLabel,
            })}
          </Title>
          <Subtitle>
            {t('checkoutPage.subtitle', {
              tokenLabel,
            })}
          </Subtitle>
        </div>

        <Section>
          <GiftCard
            token={giftCardToken}
            cardImage={cardImage}
            onCardImageChange={setCardImage}
            usdAmount={resolvedAmount}
            editable
            displayImageSelector
            hideValue={false}
          />
        </Section>

        <Section style={{ marginTop: '12px' }}>
          <AmountButtonContainer>
            {AMOUNTS.map((value) => (
              <AmountButton
                key={value}
                $active={resolvedAmount === value}
                onClick={() => {
                  setSelectedAmount(value);
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('amount', String(value));
                      return next;
                    },
                    { replace: true },
                  );
                }}
              >
                ${value}
              </AmountButton>
            ))}
          </AmountButtonContainer>
        </Section>

        <Section style={{ marginTop: '12px' }}>
          <InputWrap>
            <FieldLabel>{t('giftFlow.customize.fromOptional')}</FieldLabel>
            <TextInput
              placeholder={t('giftFlow.customize.yourNamePlaceholder')}
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
            />
          </InputWrap>
          <InputWrap>
            <FieldLabel>
              {t('giftFlow.customize.personalMessageOptional')}
            </FieldLabel>
            <TextInput
              placeholder={t(
                'giftFlow.customize.writePersonalMessagePlaceholder',
              )}
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
            />
          </InputWrap>
        </Section>

        <InfoBlock>
          <InfoTitle>{t('checkoutPage.howGiftDelivered')}</InfoTitle>
          <InfoText>{t('checkoutPage.giftDeliveredDescription')}</InfoText>
        </InfoBlock>
        <InfoBlock>
          <InfoTitle>{t('checkoutPage.howItWorks')}</InfoTitle>
          <InfoText>{t('checkoutPage.howItWorksDescription')}</InfoText>
        </InfoBlock>

        <PaySection>
          <WidthContainer>
            {applePayWidgetProps ? (
              <>
                <ApplePayWidget {...applePayWidgetProps} />
                {!isWeb && (
                  <ApplePayButton
                    onClick={handleApplePayPress}
                    loading={applePayProcessing}
                    disabled={applePayProcessing}
                  />
                )}
              </>
            ) : isWeb && hasVerifiedPhone && authenticated ? (
              (applePayStarting || applePayProcessing) && (
                <FeeText style={{ marginTop: 0 }}>
                  {t('checkoutPage.paymentProcessing')}
                </FeeText>
              )
            ) : (
              <ApplePayButton
                onClick={handleApplePayPress}
                loading={
                  oauthLoading ||
                  loading ||
                  applePayStarting ||
                  applePayProcessing ||
                  waitingForApplePayBalance
                }
                disabled={waitingForApplePayBalance}
              />
            )}
            <FeeText>
              {t('checkoutPage.applePayProcessingFee')} {formatUSD(applePayFee)}
            </FeeText>
            {feedback && (
              <FeedbackMessage $isError={feedbackIsError}>
                {feedback}
              </FeedbackMessage>
            )}
          </WidthContainer>
        </PaySection>
      </ContentContainer>
    </Content>
  );
}
